/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';

/**
 * What a person sees when something in the app throws.
 *
 * **What it replaces.** React Router's built-in fallback: a white page with
 * "Unexpected Application Error!", a raw stack trace in a grey box, and a note
 * addressed to "Hey developer 👋". Every part of that is aimed at whoever wrote
 * the app rather than whoever is using it, and it is the one screen in the
 * product where somebody is already having a bad time.
 *
 * **The stack does not go away, it goes into a fold.** Deleting it would make
 * this screen prettier and useless: the person who hits it is the only source of
 * information about a bug that has already happened, and "it broke" is not a
 * report. It is collapsed, and there is a button that copies it, because what
 * people actually do is screenshot the page and paste it into chat.
 *
 * Both buttons are plain `<a>`, not router links. Whatever went wrong may well
 * be in the router or in the state the router is holding, and asking that same
 * machinery to navigate out of its own failure is how a broken screen becomes a
 * screen you cannot leave.
 */
export function ErrorScreen() {
  const error = useRouteError();

  // Three shapes reach here: a Response thrown by the router (a 404 on a route
  // that does not exist), a real Error, and — because anything can be thrown in
  // JavaScript — whatever else somebody managed to throw.
  const detail = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? `${error.message}\n\n${error.stack ?? ''}`.trim()
      : String(error);

  const headline = isRouteErrorResponse(error) && error.status === 404
    ? 'That page does not exist.'
    : 'Something went wrong.';

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-5 py-10">
      <div className="w-full max-w-[520px]">
        <img
          src="/qollab-logo.png"
          alt="Qollab"
          width={903}
          height={218}
          className="mb-8 block h-7 w-auto"
        />

        <div className="rounded-2xl border border-border bg-surface p-7 shadow-raised motion-safe:animate-[rise_0.4s_var(--ease-out-soft)_both]">
          <div className="flex items-start gap-3.5">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-danger/10 text-danger">
              <CircleAlert className="size-5" aria-hidden />
            </span>
            <div className="min-w-0">
              <h1 className="m-0 text-xl font-bold tracking-[-0.02em]">{headline}</h1>
              {/* Says what is safe, because the first thought on seeing this
                  screen is "have I lost what I was typing". Nothing on this
                  board is stored in the page. */}
              <p className="mb-0 mt-1.5 text-base leading-relaxed text-ink-muted">
                The page hit an error it could not recover from. Your work is not
                affected — nothing on the board is kept in the browser.
              </p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2.5">
            <a
              href="/board"
              className="tap flex h-10 items-center rounded-xl bg-ink px-4 text-sm font-semibold text-bg no-underline shadow-[inset_0_1px_0_rgb(255_255_255/0.14)] transition-colors duration-fast ease-out-soft hover:bg-black"
            >
              Back to the board
            </a>
            <a
              href={typeof window === 'undefined' ? '/board' : window.location.pathname}
              className="tap flex h-10 items-center rounded-xl border border-border px-4 text-sm font-semibold text-ink-muted no-underline transition-colors duration-fast ease-out-soft hover:border-border-strong hover:text-ink"
            >
              Try this page again
            </a>
          </div>

          {detail && (
            <details className="mt-6 border-t border-border pt-4">
              <summary className="cursor-pointer select-none text-xs font-semibold text-ink-muted transition-colors duration-fast ease-out-soft hover:text-ink">
                Technical detail
              </summary>
              <pre className="mt-3 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-bg p-3 text-2xs leading-relaxed text-ink-muted">
                {detail}
              </pre>
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(detail)}
                className="tap mt-2.5 rounded-lg border border-border px-2.5 py-1.5 text-2xs font-semibold text-ink-muted transition-colors duration-fast ease-out-soft hover:border-border-strong hover:text-ink"
              >
                Copy for support
              </button>
            </details>
          )}
        </div>

        <p className="mt-5 text-center text-2xs text-ink-faint">
          If this keeps happening, send the technical detail above to whoever
          administers Qollab.
        </p>
      </div>
    </div>
  );
}
